import React, { useEffect, useState } from 'react';
import { WrappedFormUtils } from '@ant-design/compatible/lib/form/Form';
import { message } from 'antd';
import Form from '../../../../../components/form/Form';
import Panel from '../../../../../components/Panel';
import Select from '../../../../../components/input/Select';
import {
  TIMEDEAL_WIDGET_LINK_TYPE_SELECT_LIST,
  WIDGET_TYPE_LABEL_V2,
  WIDGET_TYPE_SELECT_LIST_V2,
} from '../../../../../constants/service/app/widgetConstantsV2';
import {
  WIDGET_TYPE_V2,
  TimedealWidgetDetailV2,
  TimedealWidgetRequestPayloadV2,
  TIMEDEAL_WIDGET_LINK_TYPE_V2,
} from '../../../../../types/widget';
import FormWithTab from '../../../../../components/form/Form/FormWithTab';
import WidgetLayoutSelect from '../../common/components/WidgetLayoutSelect';
import Notice from '../../../../../components/Notice';
import Input from '../../../../../components/input/Input/Input';
import TextArea from '../../../../../components/input/TextArea/TextArea';
import { DateRangePicker } from '../../../../../components/input/RangePicker';
import { DATE_FORMAT } from '../../../../../types/common/date';
import FormListTab, { TabItem } from '../../../../../components/input/FormListTab/FormListTab';
import SearchListTrigger from '../../../../../components/SearchListTrigger';
import ImageAndVideoUploadV2 from '../../../../../components/input/ImageAndVideoUploadV2';
import Switch from '../../../../../components/input/Switch';
import { useTimdealWidgetFormRules } from './useTimedealWidgetFormRules';
import LinkItem from '../../../../../components/input/LinkItem';
import { couponListFetcher } from '../../common/fetchers/couponListFetcher';
import { COUPON, PRODUCT } from '../../../../../constants/queryKeys';
import { COUPON_STATUS_LABEL_V2, COUPON_TYPE_LABEL_V2 } from '../../../../../constants/service/app/couponConstantsV2';
import { CouponItemV2, CouponListRequest } from '../../../../../types/coupons';
import { productReactTableColumns, couponReactTableColumns } from '../../common/columns';
import { productListFetcher } from '../../common/fetchers/productListFetcher';
import { getCouponPeriodText } from '../../common/utils/getCouponPeriodText';
import { useTimedealWidgetForm } from './useTimedealWidgetForm';
import LinkId from '../../../../../components/input/LinkId';
import { couponProductListFetcher } from '../../common/fetchers/couponProductListFetcher';
import WidgetFormFooter from '../../WidgetFormFooter';
import TimedealWidgetPreviewModal from './TimedealWidgetPreviewModal';
import Warning from '../../../../../components/Warning';
import { FieldError } from '../../../../../types/common/form';
import { textNormalize } from '../../../../../lib/utils/form/normalize';

interface TimedealWidgetFormProps {
  isEdit: boolean;
  form: WrappedFormUtils;
  initialValue: Partial<TimedealWidgetDetailV2>;
  onChangeType: (type: WIDGET_TYPE_V2) => void;
  onFinish: (value: TimedealWidgetRequestPayloadV2) => void;
}

const TimedealWidgetForm = (props: TimedealWidgetFormProps): JSX.Element => {
  const { initialValue, form, onFinish, onChangeType, isEdit } = props;
  const [currentTab, setCurrentTab] = useState<number>();

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  const { isUseCoupon, handleToggleIsUseCoupon } = useTimedealWidgetForm({ form });

  useEffect(() => {
    if (!currentTab && initialValue.widgetItemTimeDealList && initialValue.widgetItemTimeDealList.length)
      setCurrentTab(initialValue.widgetItemTimeDealList[0].id);
  }, [initialValue]);

  const formRule = useTimdealWidgetFormRules(form);

  const handleFinish = (value: TimedealWidgetDetailV2): void => onFinish(new TimedealWidgetRequestPayloadV2(value));

  const handleFinishFailed = (errors: FieldError[]): void => {
    const tabError = errors.find((err) => err.field === 'widgetItemTimeDealList');
    if (tabError && tabError.errors && tabError.errors.length > 0) {
      const firstErrorTab = tabError.errors[0].id;
      if (firstErrorTab) setCurrentTab(firstErrorTab);
    }
  };

  return (
    <FormWithTab
      name="widgetItemTimeDealList"
      form={form}
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
      initialValue={initialValue}
    >
      <Panel title="위젯 정보">
        <Panel.Section>
          <Form.Item
            name="type"
            label="위젯 타입"
            notice={isEdit ? <Notice>* 위젯 복사, 편집 시에는 위젯 데이터 타입을 변경할 수 없습니다.</Notice> : null}
          >
            {isEdit ? (
              <span>{WIDGET_TYPE_LABEL_V2[WIDGET_TYPE_V2.TIMEDEAL]}</span>
            ) : (
              <Select list={WIDGET_TYPE_SELECT_LIST_V2} onChange={onChangeType} style={{ width: 140 }} />
            )}
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item name="layoutType" label="위젯 레이아웃 타입">
            <WidgetLayoutSelect type={WIDGET_TYPE_V2.TIMEDEAL} />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="mainTitle"
            label="위젯 타이틀"
            rules={formRule.mainTitle.rules}
            normalize={textNormalize}
            notice={<Notice>* 위젯 타이틀의 글자 수는 최대 20자입니다.</Notice>}
          >
            <Input />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="subTitle"
            label="위젯 서브 타이틀"
            rules={formRule.subTitle.rules}
            normalize={textNormalize}
            notice={<Notice>* 위젯 서브 타이틀의 글자 수는 최대 20자입니다.</Notice>}
          >
            <Input />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item name="memo" label="메모" maxCount={1000} rules={formRule.memo.rules} normalize={textNormalize}>
            <TextArea />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item
            name="exposurePeriod"
            label={
              <span>
                위젯 노출기간
                <br />
                (=타임딜 시간)
              </span>
            }
            notice={
              <Notice>
                <p>* 해당 위젯이 메인/서브 페이지에서 노출되는 기간입니다.</p>
                <p>* 특가 상품의 할인 기간을 설정 합니다.</p>
                <p>* 타임딜 할인 기간은 최대 2일을 넘기지 말아주세요</p>
              </Notice>
            }
          >
            <DateRangePicker format={DATE_FORMAT.FULL_DASH_FORMAT} showTime={true} />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item name="isUseCoupon" label="쿠폰 사용" valuePropName="checked">
            <Switch onChange={handleToggleIsUseCoupon} />
          </Form.Item>
        </Panel.Section>
        <Panel.Section>
          <Form.Item label="노출되고 있는 페이지">
            <span>{initialValue?.currentExposurePage || '-'}</span>
          </Form.Item>
        </Panel.Section>
      </Panel>
      <Panel title="홈에서 타임딜 위젯 노출 정보를 결정하는 옵션">
        <Form.Item name="widgetItemTimeDealList" rules={formRule.widgetItemTimeDealList.rules}>
          <FormListTab
            max={18}
            notice={
              <>
                <Notice>* 드래그 앤 드랍으로 위젯 섹션의 순서를 변경할 수 있습니다.</Notice>
                <Warning>위젯에 노출된 상품 정보를 최소 1개 선택해주세요.</Warning>
              </>
            }
            initialTabItemValue={{
              mobileImage: [],
              pcImage: [],
              memo: '',
              linkType: TIMEDEAL_WIDGET_LINK_TYPE_V2.PRODUCT,
            }}
            onChangeActiveKey={setCurrentTab}
            activeKey={currentTab}
            items={(value: TimedealWidgetDetailV2['widgetItemTimeDealList']): TabItem[] =>
              value.map((item, idx) => ({
                id: item.id,
                title: `배너 - ${idx + 1}`,
                content: (tabForm) => (
                  <>
                    <Panel.Section hideIf={formRule.widgetItemTimeDealList.innerRules.coupon.hideIf}>
                      <Form.Item
                        name="coupon"
                        label="쿠폰 ID"
                        required={true}
                        rules={formRule.widgetItemTimeDealList.innerRules.coupon.rules}
                      >
                        <LinkItem<CouponItemV2, CouponListRequest>
                          fetcher={couponListFetcher}
                          queryKey={COUPON.LIST}
                          columns={couponReactTableColumns}
                          onChange={(): void => tabForm.setFieldsValue({ link: null })}
                          initialOption={{ filterOption: { type: null, status: null, discountType: null } }}
                          trigger={(openModal): JSX.Element => (
                            <SearchListTrigger
                              value={item.coupon}
                              triggerText="쿠폰 ID"
                              linkText={`${item.coupon?.name}(${item.coupon?.id})`}
                              onClick={openModal}
                              subText={
                                item.coupon
                                  ? `(상태 : ${COUPON_STATUS_LABEL_V2[item.coupon.status]} | 유형 : ${
                                      COUPON_TYPE_LABEL_V2[item.coupon.type]
                                    } | 사용기간 : ${getCouponPeriodText(item.coupon.availablePeriod)})`
                                  : ''
                              }
                            />
                          )}
                        />
                      </Form.Item>
                    </Panel.Section>
                    <Panel.Section>
                      <Form.Item
                        name="mobileImage"
                        label="App 이미지"
                        required={true}
                        rules={formRule.widgetItemTimeDealList.innerRules.mobileImage.rules}
                        notice={
                          <Notice>
                            <p>* 각 배너 별 App 홈에 노출 될 이미지입니다.</p>
                            <p>* 비율 가로 3:세로 2</p>
                            <p>
                              * 권장 크기: 1242 x 828 px (권장 크기보다 작을 경우, 웹에서 화질 저하의 우려가 있습니다.)
                            </p>
                          </Notice>
                        }
                      >
                        <ImageAndVideoUploadV2 modalTitle="APP 이미지" />
                      </Form.Item>
                    </Panel.Section>
                    <Panel.Section>
                      <Form.Item
                        name="pcImage"
                        label="PC 이미지"
                        required={true}
                        rules={formRule.widgetItemTimeDealList.innerRules.pcImage.rules}
                        notice={
                          <Notice>
                            <p>* 각 배너 별 PC 홈에 노출 될 이미지입니다.</p>
                            <p>* 비율 가로 3:세로 2</p>
                            <p>
                              * 권장 크기: 1422 x 948 px (권장 크기보다 작을 경우, 웹에서 화질 저하의 우려가 있습니다.)
                            </p>
                          </Notice>
                        }
                      >
                        <ImageAndVideoUploadV2 modalTitle="PC 이미지" />
                      </Form.Item>
                    </Panel.Section>
                    <Panel.Section>
                      <Form.Item
                        name="memo"
                        label="메모"
                        maxCount={250}
                        normalize={textNormalize}
                        rules={formRule.widgetItemTimeDealList.innerRules.memo.rules}
                      >
                        <TextArea />
                      </Form.Item>
                    </Panel.Section>
                    <Panel.Section>
                      <Form.Item name="linkType" label="링크 종류" initialValue={TIMEDEAL_WIDGET_LINK_TYPE_V2.PRODUCT}>
                        <Select list={TIMEDEAL_WIDGET_LINK_TYPE_SELECT_LIST} style={{ width: 250 }} />
                      </Form.Item>
                    </Panel.Section>
                    <Panel.Section>
                      <Form.Item
                        name="link"
                        label="링크 ID"
                        required={true}
                        rules={formRule.widgetItemTimeDealList.innerRules.link.rules}
                      >
                        <LinkId
                          fetcher={item.coupon ? couponProductListFetcher(item.coupon.id) : productListFetcher}
                          columns={productReactTableColumns}
                          queryKey={PRODUCT.LIST}
                          trigger={(openModal): JSX.Element => (
                            <SearchListTrigger
                              value={item.link}
                              triggerText="링크 ID"
                              linkText={`${item.link?.name}(${item.link?.id})`}
                              onClick={(): void => {
                                if (isUseCoupon && !item.coupon?.id) {
                                  message.error('쿠폰 정보를 선택해 주세요');
                                  return;
                                }
                                openModal();
                              }}
                            />
                          )}
                        />
                      </Form.Item>
                    </Panel.Section>
                  </>
                ),
              }))
            }
          />
        </Form.Item>
        <Form.Item>
          <WidgetFormFooter
            typeLabel={WIDGET_TYPE_LABEL_V2[WIDGET_TYPE_V2.TIMEDEAL]}
            onClickPreview={(): void => setIsPreviewModalOpen(true)}
          />
        </Form.Item>
        <TimedealWidgetPreviewModal
          isOpen={isPreviewModalOpen}
          detail={form.getFieldsValue() as TimedealWidgetDetailV2}
          onClose={(): void => setIsPreviewModalOpen(false)}
        />
      </Panel>
    </FormWithTab>
  );
};

export default Form.create<TimedealWidgetFormProps>()(TimedealWidgetForm);
